import { Component, TooltipOptions } from "@mcleod/components";
import {
    Alignment, Api, ArrayUtil, GeneralSettings, LocalUserSettings, MenuItem, ObjectUtil, StringUtil
} from "@mcleod/core";
import { CommonDialogs } from "../CommonDialogs";
import { RowUserMenuFavorite } from "../models/ModelUserMenuFavorite";
import { RowUserMenuFavoriteFields } from "../models/autogen/AutogenModelUserMenuFavorite";
import { MainMenu } from "./MainMenu";

export type FavoritesChangedCallback = (favorites: RowUserMenuFavorite[]) => void;

const FAVORITES_STORAGE_KEY = "menu.favorites";

interface StoredFavorite {
    id: string;
    path: string;
}

export class MenuFavorites {
    private static readonly favoritesChangedCallbacks = new Set<FavoritesChangedCallback>();
    private static userFavoriteRows: RowUserMenuFavorite[];

    private static initializeFavoritesIfNeeded() {
        if (MenuFavorites.userFavoriteRows != null)
            return;

        // Set up storage event listener
        window.addEventListener("storage", (event: StorageEvent) => {
            if (event.key === FAVORITES_STORAGE_KEY) {
                const storedFavorites = LocalUserSettings.getArray<StoredFavorite>(FAVORITES_STORAGE_KEY);
                MenuFavorites.refreshFavorites(storedFavorites);
                MenuFavorites.fireFavoritesChangedCallbacks(MenuFavorites.userFavoriteRows);
            }
        });

        MenuFavorites.refreshFavorites(GeneralSettings.get().menu_favorites);
        MenuFavorites.saveFavorites();
    }

    public static clearFavorites() {
        MenuFavorites.userFavoriteRows = null;
    }

    private static refreshFavorites(favoritesToProcess: StoredFavorite[]) {
        MenuFavorites.userFavoriteRows = [];
        favoritesToProcess?.forEach(favorite => {
            favorite = ObjectUtil.deleteNullOrEmptyProps(favorite);
            const menuItem = MainMenu.findMenuItemForPath(favorite.path);
            if (menuItem != null) {
                const row = RowUserMenuFavorite.createFromMenuItem(menuItem, true, favorite);
                MenuFavorites.userFavoriteRows.push(row);
            }
        });
        MenuFavorites.sortFavorites();
    }

    private static saveFavorites() {
        // Only store the minimal required data in local storage
        const favoritesToStore = MenuFavorites.userFavoriteRows.map(fav => ({
            id: fav.get("id"),
            path: fav.get("path")
        }));
        LocalUserSettings.set(FAVORITES_STORAGE_KEY, favoritesToStore);
    }

    private static sortFavorites() {
        MenuFavorites.userFavoriteRows.sort((a, b) => a.get("caption").localeCompare(b.get("caption")));
    }

    public static getFavoriteCount(): number {
        MenuFavorites.initializeFavoritesIfNeeded();
        return ArrayUtil.getLength(MenuFavorites.userFavoriteRows);
    }

    private static getIndexOfFavorite(fieldValue: string, matchField: keyof RowUserMenuFavoriteFields): number {
        MenuFavorites.initializeFavoritesIfNeeded();
        return MenuFavorites.userFavoriteRows?.findIndex(fave => fave.get(matchField) === fieldValue) ?? -1;
    }

    public static isFavorite(item: MenuItem) {
        MenuFavorites.initializeFavoritesIfNeeded();
        return MenuFavorites.getIndexOfFavorite(item.path, "path") >= 0;
    }

    public static getFavorites(): RowUserMenuFavorite[] {
        MenuFavorites.initializeFavoritesIfNeeded();
        return ArrayUtil.copy(MenuFavorites.userFavoriteRows);
    }

    /**
     * Adds or removes a favorite from the user's favorites list
     * @param anchor - The component to anchor the tooltip to
     * @param item - The menu item to add or remove from the favorites list
     */
    public static async addOrRemoveFavorite(anchor: Component, item: MenuItem) {
        const favItem = MenuFavorites.userFavoriteRows?.find(favorite => favorite.get("path") === item.path);
        if (favItem != null) {
            await MenuFavorites.removeFavorite(favItem.get("id"));
        } else {
            await MenuFavorites.addFavorite(anchor, item);
        }
    }

    /**
     * Adds a favorite to the user's favorites list
     * @param anchor - The component to anchor the tooltip to
     * @param menuItem - The menu item to add to the favorites list
     */
    public static async addFavorite(anchor: Component, menuItem: MenuItem) {
        const favoriteItem = RowUserMenuFavorite.createFromMenuItem(menuItem);
        const tooltipOptions: TooltipOptions = { timeout: 2500, position: Alignment.RIGHT };
        if (MenuFavorites.getIndexOfFavorite(menuItem.path, "path") >= 0) {
            anchor.showAdhocTooltip("This page is already in your favorites!", tooltipOptions)
            return;
        }
        await favoriteItem.post();
        favoriteItem.set("caption", menuItem.favoriteCaption ?? menuItem.caption);
        anchor.showAdhocTooltip("This page has been added to your favorites!", tooltipOptions);

        MenuFavorites.userFavoriteRows.push(favoriteItem);
        MenuFavorites.sortFavorites();
        MenuFavorites.fireFavoritesChangedCallbacks(MenuFavorites.userFavoriteRows);
        MenuFavorites.saveFavorites();
    }

    /**
     * Removes a favorite from the user's favorites list
     * @param userFavoriteId - The ID of the favorite to remove
     * @returns Promise that resolves when the favorite is removed
     */
    public static async removeFavorite(userFavoriteId: string): Promise<void> {
        if (StringUtil.isEmptyString(userFavoriteId)) {
            return;
        }

        if (MenuFavorites.getIndexOfFavorite(userFavoriteId, "id") < 0) {
            return;
        }

        if (await CommonDialogs.showYesNo("Are you sure you want to remove this favorite?", "Confirm Removal")) {
            // Check the index again after user confirms, in case it was deleted in another tab
            const index = MenuFavorites.getIndexOfFavorite(userFavoriteId, "id");
            if (index < 0) {
                return;
            }

            await Api.delete("common/user-menu-favorite", { id: userFavoriteId }, null, null);
            MenuFavorites.userFavoriteRows.splice(index, 1);
            MenuFavorites.fireFavoritesChangedCallbacks(MenuFavorites.userFavoriteRows);
            MenuFavorites.saveFavorites();
        }
    }

    public static addFavoritesChangedCallback(callback: FavoritesChangedCallback) {
        MenuFavorites.favoritesChangedCallbacks.add(callback);
    }

    public static removeFavoritesChangedCallback(callback: FavoritesChangedCallback) {
        MenuFavorites.favoritesChangedCallbacks.delete(callback);
    }

    public static fireFavoritesChangedCallbacks(favorites: RowUserMenuFavorite[]) {
        MenuFavorites.favoritesChangedCallbacks.forEach(callback => callback(favorites));
    }
}
